import { ApolloProvider } from '@apollo/client'
// CSS Imports
import 'antd/dist/antd.dark.less'
import Gleap from 'gleap'
import { useEffect, useMemo, useState } from 'react'
import { SkeletonTheme } from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'
import 'simplebar-react/dist/simplebar.min.css'

import 'src/app.css'
import RedirectToLogin from 'src/components/common/RedirectToLogin'
import 'src/components/common/common.css'
import MobilePlaceholder from 'src/components/common/mobile-placeholder'
// Common components
import PrivateRoute from 'src/components/common/private-route'
import Spinner from 'src/components/common/spinner'
import { useMediaQueryUtils } from 'src/constants/exportHooks'
// Context Objects
import AccessTokenContext from 'src/context/accessTokenContext'
import { SubscriptionProvider } from 'src/context/billing.context'
import { BuildContextProvider } from 'src/context/buildContext'
// Context providers
import { DummyDataContextProvider } from 'src/context/dummyData'
import { OrgSizeProvider } from 'src/context/org-size-ctx'
import OrganizationContext from 'src/context/organizationContext'
import RedirectSignupContext from 'src/context/redirectSignupContext'
import { SimulationCtxProvider } from 'src/context/simulationCtx'
import UserEmailContext from 'src/context/userEmailContext'
import { APP_ENV } from 'src/env'
import 'src/gleap.css'
// Routes config
import routes from 'src/routes'
import { cn } from 'src/utils/cn'
import useAuthState, { AuthEnum } from 'src/utils/hooks/useAuthState'
import { useLocalStorage } from 'src/utils/hooks/useLocalStorage'
import useLogout from 'src/utils/hooks/useLogout'
import useORNAApolloClient from 'src/utils/hooks/useORNAApolloClient'
import useSimulation from 'src/utils/hooks/useSimulation'
import useSimulationActivePing from 'src/utils/hooks/useSimulationActivePing'
import useTTXUserData from 'src/utils/hooks/useTTXUser'
import logger from 'src/utils/logger'
import { CYPRESS_OPTIONS, IS_CYPRESS } from 'src/utils/misc'
// Public/Hybrid Route components
import AcceptInviteContainer from 'src/views/AcceptInvite'
import BackstagePage from 'src/views/Backstage'
// Public Route components
import ForgotPasswordPage from 'src/views/ForgotPassword'
import ResetPasswordPage from 'src/views/ForgotPassword/resetPassword'
import LoginPage from 'src/views/Login'
// Mobile Route Components
import Mobile from 'src/views/Mobile'
import MonitorPage from 'src/views/Monitor'
import PageNotFoundPage from 'src/views/Pagenotfound'
import PlanPage from 'src/views/Plan'
import PracticePage from 'src/views/Practice'
import RequestEmailPage from 'src/views/RequestEmail'
import RespondPage from 'src/views/Respond'
import SelectOrganization from 'src/views/SelectOrganization'
import SettingsPage from 'src/views/Settings'
import SignupPage from 'src/views/Signup'
import ViewReportArtifactPage from 'src/views/ViewReportArtifact'

import TTXUserCtxProvider from './context/ttx-user.ctx.provider'
import InvalidInviteLink from './views/InvalidInviteLink'

const App = () => {
    const { isMobile } = useMediaQueryUtils()
    const ttxInviteToken = useMemo(() => {
        const searchParams = new URL(window.location.href).searchParams

        return searchParams.get('ttxInviteToken')
    }, [])

    const isTTXUser = !!ttxInviteToken

    const [navigateTo, setNavigateTo] = useState(routes.plan)
    const [organizationId__fromAuth, setOrganizationId__fromAuth] = useState(
        () => {
            if (IS_CYPRESS) {
                return CYPRESS_OPTIONS.userOrganizationId
            }

            const id = localStorage.getItem('userOrganizationId')

            return !!id && typeof id === 'string' && id !== 'null' ? id : null
        }
    )

    useLocalStorage(organizationId__fromAuth, 'userOrganizationId')

    const ttxUserData = useTTXUserData(ttxInviteToken)

    const {
        authState,
        userEmail: userEmail__fromAuth,
        authToken,
        authTokenError,
    } = useAuthState(organizationId__fromAuth)

    const logout = useLogout()

    // read redirected signup flow state & add to context provider
    const [redirectState, setRedirectState] = useState(() => {
        const redirectData = localStorage.getItem('redirectFromSignup')
        const state = JSON.parse(localStorage.getItem('redirectFromSignup'))
        //TODO: Remove signup state after either a successful signup or after user chooses an org
        if (!redirectData) return null
        return state
    })

    const { isSimulationEnabled, setIsSimulationEnabled, simulationId } =
        useSimulation(authToken, organizationId__fromAuth)

    const apolloClient = useORNAApolloClient({
        accessToken: authToken,
        organizationId: organizationId__fromAuth,
        isTTXUser,
        ttxInviteToken,
        isSimulationEnabled,
        simulationId,
    })

    useSimulationActivePing(isSimulationEnabled, apolloClient)

    // logout the user in case of auth0 sdk error
    useEffect(() => {
        if (authState === AuthEnum.error) {
            logger.error(new Error('auth0sdk error, logging out the user'))
            logout()
        }
    }, [authState, logout])

    useEffect(() => {
        if (authTokenError) {
            logger.error(authTokenError)
            logout()
        }
    }, [authTokenError, logout])

    useEffect(() => {
        if (authState !== 'orgUser') {
            Gleap.showFeedbackButton(false)
            return
        }

        Gleap.showFeedbackButton(true)
    }, [authState])

    if (ttxUserData.tokenError) {
        logger.error(ttxUserData.tokenError)
        return <InvalidInviteLink />
    }

    if (isTTXUser) {
        return (
            <TTXUserCtxProvider
                userId={ttxUserData.id}
                incidentId={ttxUserData.incidentId}
                simulationId={ttxUserData.simulationId}
            >
                <MobilePlaceholder />
                <div
                    className={cn(
                        'block h-full w-full',
                        APP_ENV.REACT_APP_LOCAL === 'no' &&
                            APP_ENV.REACT_APP_ENVIRONMENT === 'production' &&
                            'hidden lg:block'
                    )}
                >
                    <OrganizationContext.Provider
                        value={{
                            organizationId: ttxUserData.organizationId,
                            setOrganizationId: setOrganizationId__fromAuth,
                        }}
                    >
                        <UserEmailContext.Provider
                            value={{
                                userEmail: ttxUserData.email,
                            }}
                        >
                            <AccessTokenContext.Provider
                                value={{
                                    accessToken: null,
                                }}
                            >
                                <SkeletonTheme
                                    baseColor="#242E42"
                                    highlightColor="#2F3B52"
                                >
                                    <ApolloProvider client={apolloClient}>
                                        <SimulationCtxProvider
                                            simulationId={
                                                ttxUserData.simulationId
                                            }
                                            isEnabled={true}
                                            setEnabled={setIsSimulationEnabled}
                                        >
                                            <BuildContextProvider>
                                                <DummyDataContextProvider>
                                                    <OrgSizeProvider>
                                                        <SubscriptionProvider>
                                                            <BrowserRouter>
                                                                <Switch>
                                                                    <PrivateRoute
                                                                        path={
                                                                            routes.practice
                                                                        }
                                                                        component={
                                                                            PracticePage
                                                                        }
                                                                    />
                                                                    <Route>
                                                                        <Redirect
                                                                            to={`/practice/simulations/${ttxUserData.incidentId}?${new URLSearchParams({ ttxInviteToken }).toString()}`}
                                                                        />
                                                                    </Route>
                                                                </Switch>
                                                            </BrowserRouter>
                                                        </SubscriptionProvider>
                                                    </OrgSizeProvider>
                                                </DummyDataContextProvider>
                                            </BuildContextProvider>
                                        </SimulationCtxProvider>
                                    </ApolloProvider>
                                </SkeletonTheme>
                            </AccessTokenContext.Provider>
                        </UserEmailContext.Provider>
                    </OrganizationContext.Provider>
                </div>
            </TTXUserCtxProvider>
        )
    }

    if (authState === AuthEnum.loading) {
        return <Auth0Loading text="Auth0Loading" />
    }

    if (authState === AuthEnum.error) {
        return (
            <div className="flex h-screen w-screen items-center justify-center text-center text-3xl">
                <Spinner
                    loadingText={
                        <h3 className="text-lg font-bold text-primary-red-500">
                            Your session has expired, logging you out...
                        </h3>
                    }
                />
            </div>
        )
    }

    if (authTokenError) {
        return (
            <div className="flex h-screen w-screen items-center justify-center text-center text-3xl">
                <Spinner
                    loadingText={
                        <h3 className="text-lg font-bold text-primary-red-500">
                            Your session has expired, logging you out...
                        </h3>
                    }
                />
            </div>
        )
    }

    // show org selector
    if (authState === AuthEnum.user) {
        if (!authToken) {
            return <Auth0Loading text="Loading Token" />
        }

        return (
            <>
                <MobilePlaceholder />
                <div
                    className={cn(
                        'block h-full w-full',
                        APP_ENV.REACT_APP_LOCAL === 'no' &&
                            APP_ENV.REACT_APP_ENVIRONMENT === 'production' &&
                            'hidden lg:block'
                    )}
                >
                    <AccessTokenContext.Provider
                        value={{
                            accessToken: authToken,
                        }}
                    >
                        <OrganizationContext.Provider
                            value={{
                                organizationId: organizationId__fromAuth,
                                setOrganizationId: setOrganizationId__fromAuth,
                            }}
                        >
                            <UserEmailContext.Provider
                                value={{
                                    userEmail: userEmail__fromAuth,
                                }}
                            >
                                <RedirectSignupContext.Provider
                                    displayName="RedirectSignupContextProvider"
                                    value={{
                                        redirectState,
                                        setRedirectState,
                                    }}
                                >
                                    <SkeletonTheme
                                        baseColor="#242E42"
                                        highlightColor="#2F3B52"
                                    >
                                        <BrowserRouter>
                                            <Switch>
                                                <ViewReportArtifactPageRoute
                                                    isMobile={isMobile}
                                                    exact
                                                    path={
                                                        routes.viewReportArtifact
                                                    }
                                                />
                                                <AcceptInviteContainerRoute
                                                    isMobile={isMobile}
                                                    exact
                                                    path={routes.acceptInvite}
                                                />
                                                {redirectState && (
                                                    <Route
                                                        path={routes.signup}
                                                        exact
                                                    >
                                                        {() => {
                                                            return (
                                                                <SignupPage />
                                                            )
                                                        }}
                                                    </Route>
                                                )}
                                                <Route
                                                    exact
                                                    path={
                                                        routes.selectOrganization
                                                    }
                                                    render={(props) => (
                                                        <SelectOrganization
                                                            setNavigateTo={
                                                                setNavigateTo
                                                            }
                                                            {...props}
                                                        />
                                                    )}
                                                />
                                                <Route
                                                    render={({ location }) => {
                                                        const searchParams =
                                                            new URLSearchParams(
                                                                {
                                                                    from: location.pathname,
                                                                }
                                                            )

                                                        return (
                                                            <Redirect
                                                                to={`${routes.selectOrganization}?${searchParams.toString()}`}
                                                            />
                                                        )
                                                    }}
                                                />
                                            </Switch>
                                        </BrowserRouter>
                                    </SkeletonTheme>
                                </RedirectSignupContext.Provider>
                            </UserEmailContext.Provider>
                        </OrganizationContext.Provider>
                    </AccessTokenContext.Provider>
                </div>
            </>
        )
    }

    // we are good to go
    if (authState === AuthEnum.cypress || authState === AuthEnum.orgUser) {
        return (
            <>
                <MobilePlaceholder />
                <div
                    className={cn(
                        'block h-full w-full',
                        APP_ENV.REACT_APP_LOCAL === 'no' &&
                            APP_ENV.REACT_APP_ENVIRONMENT === 'production' &&
                            'hidden lg:block'
                    )}
                >
                    <OrganizationContext.Provider
                        value={{
                            organizationId: organizationId__fromAuth,
                            setOrganizationId: setOrganizationId__fromAuth,
                        }}
                    >
                        <UserEmailContext.Provider
                            value={{
                                userEmail: userEmail__fromAuth,
                            }}
                        >
                            <AccessTokenContext.Provider
                                value={{
                                    accessToken: authToken,
                                }}
                            >
                                <SkeletonTheme
                                    baseColor="#242E42"
                                    highlightColor="#2F3B52"
                                >
                                    <ApolloProvider client={apolloClient}>
                                        <SimulationCtxProvider
                                            simulationId={simulationId}
                                            isEnabled={isSimulationEnabled}
                                            setEnabled={setIsSimulationEnabled}
                                        >
                                            <BuildContextProvider>
                                                <DummyDataContextProvider>
                                                    <OrgSizeProvider>
                                                        <SubscriptionProvider>
                                                            <BrowserRouter>
                                                                <Switch>
                                                                    <ViewReportArtifactPageRoute
                                                                        isMobile={
                                                                            isMobile
                                                                        }
                                                                        exact
                                                                        path={
                                                                            routes.viewReportArtifact
                                                                        }
                                                                    />
                                                                    <AcceptInviteContainerRoute
                                                                        isMobile={
                                                                            isMobile
                                                                        }
                                                                        exact
                                                                        path={
                                                                            routes.acceptInvite
                                                                        }
                                                                    />
                                                                    <ResetPasswordContainerRoute
                                                                        isMobile={
                                                                            isMobile
                                                                        }
                                                                        exact
                                                                        path={
                                                                            routes.forgotPassword
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path={
                                                                            routes.backstage
                                                                        }
                                                                    >
                                                                        <BackstagePage />
                                                                    </Route>
                                                                    <PrivateRoute
                                                                        path={
                                                                            routes.plan
                                                                        }
                                                                        component={
                                                                            PlanPage
                                                                        }
                                                                    />
                                                                    <PrivateRoute
                                                                        path={
                                                                            routes.practice
                                                                        }
                                                                        component={
                                                                            PracticePage
                                                                        }
                                                                    />
                                                                    <PrivateRoute
                                                                        path={
                                                                            routes.respond
                                                                        }
                                                                        component={
                                                                            RespondPage
                                                                        }
                                                                    />
                                                                    <PrivateRoute
                                                                        path={
                                                                            routes.monitor
                                                                        }
                                                                        component={
                                                                            MonitorPage
                                                                        }
                                                                    />
                                                                    <PrivateRoute
                                                                        path={
                                                                            routes.settings
                                                                        }
                                                                        component={
                                                                            SettingsPage
                                                                        }
                                                                    />
                                                                    <Route
                                                                        path={[
                                                                            routes.login,
                                                                            routes.signup,
                                                                            routes.selectOrganization,
                                                                        ]}
                                                                    >
                                                                        <Redirect
                                                                            to={{
                                                                                pathname:
                                                                                    routes.plan,
                                                                            }}
                                                                        />
                                                                    </Route>
                                                                    <Route
                                                                        exact
                                                                        path={
                                                                            '/'
                                                                        }
                                                                    >
                                                                        <Redirect
                                                                            to={{
                                                                                pathname:
                                                                                    navigateTo !==
                                                                                    '/'
                                                                                        ? navigateTo
                                                                                        : routes.plan,
                                                                            }}
                                                                        />
                                                                    </Route>
                                                                    <PrivateRoute
                                                                        component={
                                                                            PageNotFoundPage
                                                                        }
                                                                    />
                                                                </Switch>
                                                            </BrowserRouter>
                                                        </SubscriptionProvider>
                                                    </OrgSizeProvider>
                                                </DummyDataContextProvider>
                                            </BuildContextProvider>
                                        </SimulationCtxProvider>
                                    </ApolloProvider>
                                </SkeletonTheme>
                            </AccessTokenContext.Provider>
                        </UserEmailContext.Provider>
                    </OrganizationContext.Provider>
                </div>
            </>
        )
    }

    return (
        <OrganizationContext.Provider
            value={{
                organizationId: organizationId__fromAuth,
                setOrganizationId: setOrganizationId__fromAuth,
            }}
        >
            <RedirectSignupContext.Provider
                value={{
                    redirectState,
                    setRedirectState,
                }}
            >
                <SkeletonTheme baseColor="#242E42" highlightColor="#2F3B52">
                    <BrowserRouter>
                        <Switch>
                            <Route
                                path={routes.login}
                                exact
                                render={(props) => <LoginPage {...props} />}
                            />
                            <Route path={routes.signup} exact>
                                <SignupPage />
                            </Route>
                            <Route path={routes.forgotPassword} exact>
                                <ForgotPasswordPage />
                            </Route>
                            <ResetPasswordContainerRoute
                                isMobile={isMobile}
                                exact
                                path={routes.passwordReset}
                            />
                            <ViewReportArtifactPageRoute
                                isMobile={isMobile}
                                exact
                                path={routes.viewReportArtifact}
                            />
                            <AcceptInviteContainerRoute
                                isMobile={isMobile}
                                exact
                                path={routes.acceptInvite}
                            />
                            <RequestEmailPageRoute
                                isMobile={isMobile}
                                exact
                                path={routes.requestEmail}
                            />
                            <Route
                                render={(props) => {
                                    return <RedirectToLogin {...props} />
                                }}
                            />
                        </Switch>
                    </BrowserRouter>
                </SkeletonTheme>
            </RedirectSignupContext.Provider>
        </OrganizationContext.Provider>
    )
}

const Auth0Loading = ({ text }) => {
    return (
        <div
            className="h-screen w-screen"
            style={{
                background: '#242e42',
            }}
        >
            <Spinner loadingText={APP_ENV.REACT_APP_LOCAL === 'yes' && text} />
        </div>
    )
}

const AcceptInviteContainerRoute = ({ isMobile, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            return isMobile ? <Mobile /> : <AcceptInviteContainer {...props} />
        }}
    ></Route>
)

const ResetPasswordContainerRoute = ({ isMobile, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            return isMobile ? <Mobile /> : <ResetPasswordPage {...props} />
        }}
    ></Route>
)

const RequestEmailPageRoute = ({ isMobile, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            return isMobile ? <Mobile /> : <RequestEmailPage {...props} />
        }}
    />
)

const ViewReportArtifactPageRoute = ({ isMobile, ...rest }) => (
    <Route
        {...rest}
        render={(props) => {
            return isMobile ? <Mobile /> : <ViewReportArtifactPage {...props} />
        }}
    />
)

export default App
